import { fabric } from 'fabric'
import type { Subscription } from 'dexie'
import { liveQuery } from 'dexie'
import { v4 as guid } from 'uuid'
import { pick } from 'lodash-es'
import type CatalogDetails from '@/models/catalogDetails'
import type MyArticle from '@/models/myArticle'
import appConfig from '@/services/appConfig'
import utils from '@/services/utils'
import { useUserStore } from '@/store/userData'
import { whiteboardConstants } from '@/models/constants'

interface IWbModelDetailsOptions extends fabric.ITextboxOptions, IWbObjectProps {
  id?: string
  catalogCode?: number
  modelNumber?: string
  showLabels?: boolean
  lock?: boolean
  attributes?: string[]
  attributesSeparator?: string
  attributePlaceHolder?: string
  preventUnlock?: boolean
  localOnly?: boolean
  preventDelete?: boolean
  excludeFromGroupSelection?: boolean
}

// eslint-disable-next-line ts/ban-ts-comment
// @ts-expect-error
export default class WbModelDetails extends fabric.Textbox implements IWbObject {
  public id: string
  public catalogCode?: number
  public modelNumber?: string
  public article?: MyArticle
  public showLabels: boolean
  public attributes?: string[]
  public attributesSeparator?: string
  public attributePlaceHolder?: string
  public localOnly?: boolean
  public type = whiteboardConstants.objectTypes.modelDetails
  public lock: boolean
  public preventUnlock: boolean
  public connectable: boolean
  public preventDelete?: boolean
  public excludeFromGroupSelection?: boolean
  private isLoadingFirstTime: boolean = true
  public canBeLinkedTo = false
  public canLink = false
  public link = undefined
  public editableProps: Record<string, IWbObjectProp> = {
    textColor: { name: 'text color', type: 'textColor', editable: true },
    font: { name: 'font', type: 'font', editable: true },
    fontSize: { name: 'font size', type: 'fontSize', editable: true },
    textAlign: { name: 'align', type: 'textAlign', editable: true },
    fontStyle: { name: 'font style', type: 'fontStyle', editable: true },
    lock: { name: 'Lock', type: 'lock', editable: true, editableWhenLocked: true },
    showLabels: { name: 'show labels', type: 'showLabels', editable: true },
    attributes: { name: 'attributes', type: 'attributes', editable: true },
    addDiscussion: { name: 'add comment', type: 'addDiscussion', editable: true, editableWhenLocked: true },
  }

  public actions: Record<string, IWObjectActions> = {
    selectSimilar: { action: 'selectSimilar', label: 'Select Similar', faicon: 'fa-light fa-check-double', showInSubMenu: true },
    bringFront: { action: 'bringFront', label: 'Bring to Front', faicon: 'fa-light fa-bring-front', showInSubMenu: true },
    sendBack: { action: 'sendBack', label: 'Send to Back', faicon: 'fa-light fa-send-back', showInSubMenu: true },
    // group: { action: 'group', label: 'Group', faicon: 'fa-light fa-object-group', showInSubMenu: true, multiple: true },
    cloneAsImage: { action: 'cloneAsImage', label: 'Clone As Image', faicon: 'fa-light fa-clone', showInSubMenu: true },
    delete: { action: 'delete', label: 'Remove', faicon: 'fa-light fa-trash-can', showInSubMenu: true },
  }

  public static defaultAttributes = ['ModelNumber', 'ModelName']
  public subscriptions: Array<Subscription>

  constructor(text: string, options?: IWbModelDetailsOptions) {
    super(text, options)
    this.id = options?.id || guid()
    this.catalogCode = options?.catalogCode
    this.modelNumber = options?.modelNumber
    this.showLabels = utils.isDefined(options) && utils.isDefined(options.showLabels) ? options.showLabels : true
    this.attributes = options?.attributes?.length ? options.attributes : [...WbModelDetails.defaultAttributes]
    this.attributesSeparator = options?.attributesSeparator || '\n'
    this.attributePlaceHolder = options?.attributePlaceHolder || ''
    this.fontFamily = options?.fontFamily || 'Helvetica'
    this.fontSize = options?.fontSize || 14
    this.textAlign = options?.textAlign || 'center'
    this.lock = options?.lock || false
    this.editable = false
    this.connectable = true
    this.localOnly = options?.localOnly || false
    this.subscriptions = []
    this.preventUnlock = options?.preventUnlock || false
    this.preventDelete = options?.preventDelete || false
    this.excludeFromGroupSelection = options?.excludeFromGroupSelection || false

    const userStore = useUserStore()
    const catalogDetails = this.catalogCode ? (userStore.linkedCatalogDetails[this.catalogCode] || userStore.activeCatalog) : userStore.activeCatalog!
    // if previous season model article need to add the current season article as well in the live query to support the updates in sku count of current catalog.
    const queryCriterion: Array<[number, number, string]> = [[userStore.activeCatalog!.CatalogCode, 1, this.modelNumber || '']]
    if (catalogDetails.CatalogCode !== userStore.activeCatalog?.CatalogCode) {
      queryCriterion.push([+catalogDetails!.CatalogCode, 1, this.modelNumber || ''])
    }
    const observable = liveQuery(async () => await appConfig.DB!.articles.where('[CatalogCode+Status+ModelNumber]').anyOf(queryCriterion).toArray())
    const subscription = observable.subscribe(async (response) => {
      // As discussed with Andre we dont need to set article again when we first time create the object as already we are loading the article
      if (!this.isLoadingFirstTime) {
        const article = response.find(article => article.CatalogCode === catalogDetails.CatalogCode)
        if (article) {
          const articles = await appConfig.DB!.buildMyArticles([article], catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, true)
          this.article = articles[0]
          this.set('text', await WbModelDetails.getText(this.article, this.showLabels, this.attributes, this.attributesSeparator, this.attributePlaceHolder))
          this.dirty = true
          this.canvas?.requestRenderAll()
          this.canvas?.fire('object:modified', { target: this, ignoreHistory: true })
        }
      }
      else {
        this.isLoadingFirstTime = false
      }
    })
    this.subscriptions.push(subscription)

    this.set('text', text) // super(text, options) is not setting the text property with the text value rather it display the static value received from server
    this.setLock(this.lock)

    this.on('removed', () => {
      // console.log('unsubscribing from model details observables')
      this.subscriptions.forEach((subscription) => {
        if (subscription && !subscription.closed) {
          subscription.unsubscribe()
        }
      })
    })
    this.setLock(this.lock)

    this.on('selected', () => {
      this.selected = true
    })

    this.on('deselected', () => {
      this.selected = false
    })
    this.stateProperties = this.stateProperties?.concat(['showLabels', 'lock', 'attributes', 'preventUnlock'])
  }

  async setProp(prop: string, value: any, ignoreHistory: boolean = false) {
    const userStore = useUserStore()
    const catalogDetails = userStore.linkedCatalogDetails[this.catalogCode!] || userStore.activeCatalog
    switch (prop) {
      case 'top':
        this.set('top', value.top)
        break
      case 'left':
        this.set('left', value.left)
        break
      case 'textColor':
        this.set('fill', value.textColor)
        break
      case 'font':
        this.set('fontFamily', value.font)
        break
      case 'fontSize':
        this.set('fontSize', value.fontSize)
        break
      case 'textAlign':
        this.set('textAlign', value.textAlign)
        break
      case 'bold':
        this.set('fontWeight', value.bold ? 'bold' : '')
        break
      case 'italic':
        this.set('fontStyle', value.italic ? 'italic' : '')
        break
      case 'underline':
        this.set('underline', value.underline)
        break
      case 'lock':
        this.setLock(value.lock)
        break
      case 'showLabels':
        this.set('showLabels', value.showLabels)
        this.set('text', await this.getAttributesValue(this.modelNumber, catalogDetails, this.attributes, value.showLabels))
        break
      case 'attributes':
        this.set('attributes', value.attributes)
        this.set('text', await this.getAttributesValue(this.modelNumber, catalogDetails, value.attributes, this.showLabels))
        break
      case 'text':
        // value in case of set text value should be fetch from article
        this.set('text', await this.getAttributesValue(this.modelNumber, catalogDetails, this.attributes, this.showLabels))
        break
      case 'height':
        this.set('height', value)
        break
      default:
        console.warn('Attempting to set unsupported WbObjectProp', prop, value)
        return
    }
    this.dirty = true
    this.canvas?.requestRenderAll()
    this.canvas?.fire('object:modified', { target: this, ignoreHistory })
  }

  getProp(prop: string) {
    const result: any = {}
    switch (prop) {
      case 'textColor':
        result.textColor = this.fill
        break
      case 'font':
        result.font = this.fontFamily
        break
      case 'fontSize':
        result.fontSize = this.fontSize
        break
      case 'textAlign':
        result.textAlign = this.textAlign
        break
      case 'bold':
        result.bold = !!this.fontWeight
        break
      case 'italic':
        result.italic = !!this.fontStyle
        break
      case 'underline':
        result.underline = !!this.underline
        break
      case 'lock':
        result.lock = this.lock
        break
      case 'showLabels':
        result.showLabels = this.showLabels
        break
      case 'attributes':
        result.attributes = this.attributes
        break
      case 'text':
        result.text = this.text
        break
      default:
        console.warn('Attempting to get unsupported WbObjectProp', prop)
    }
    return result
  }

  setLock(lock: boolean) {
    this.set('lock', lock)
    this.set('lockMovementX', lock)
    this.set('lockMovementY', lock)
    this.set('lockRotation', lock)
    this.set('lockScalingFlip', lock)
    this.set('lockScalingX', lock)
    this.set('lockScalingY', lock)
    this.set('hasControls', !lock)
  }

  override toObject() {
    const propsToPluck = [
      'id',
      'type',
      'top',
      'left',
      'angle',
      'width',
      'height',
      'scaleX',
      'scaleY',
      'fill',
      'backgroundColor',
      'fontFamily',
      'fontSize',
      'fontStyle',
      'underline',
      'fontWeight',
      'textAlign',
      'lock',
      'showLabels',
      'modelNumber',
      'catalogCode',
      'attributes',
      'attributesSeparator',
      'attributePlaceHolder',
      'preventUnlock',
      'localOnly',
    ]
    const returnObject = pick(this, propsToPluck)
    return returnObject
  }

  static fromObject(object: WbModelDetails, callback?: Function) {
    if (object.modelNumber != null && object.catalogCode != null) {
      this.loadModelDetailsByNumber(object.modelNumber, object.catalogCode, object)
        .then((v) => { callback && callback(v) })
    }
  }

  // this function will always resolve to a value
  static async getText(article: MyArticle, showLabels: boolean, attributes?: string[], attributesSeparator?: string, attributePlaceHolder?: string) {
    const userStore = useUserStore()
    const attributeData: any[] = []
    if (attributes && attributes.length) {
      for (let i = 0; i < attributes.length; i++) {
        const attributeSystemName = attributes[i]
        let attributeValue
        if (utils.isDefined(userStore.myAttributes) && utils.isDefined(userStore.myAttributes[attributeSystemName]) && userStore.myAttributes[attributeSystemName].IsModelLevel) {
          attributeValue = await utils.getAttributeTypeSpecificValue(userStore.myAttributes![attributeSystemName], article, appConfig.DB, userStore.activeCatalog, userStore.priceGroups)
          if (!utils.isValidStringValue(attributeValue)) {
            attributeValue = attributePlaceHolder
          }
          let label = ''
          if (showLabels && userStore.activeCatalog) {
            const whiteboardLabelAttributeConfigValue = userStore.activeCatalog.Config.WhiteboardLabelAttributes
            if (whiteboardLabelAttributeConfigValue === undefined
              || (utils.isDefined(whiteboardLabelAttributeConfigValue) && Array.isArray(whiteboardLabelAttributeConfigValue) && whiteboardLabelAttributeConfigValue!.includes(userStore.myAttributes[attributeSystemName].SystemName))) {
              label = userStore.myAttributes[attributeSystemName].DisplayName
            }
          }
          attributeData.push(showLabels && label !== '' ? `${label}: ${attributeValue}` : attributeValue)
        }
        else if (userStore.activeCatalog!.CatalogCode.toString() === attributeSystemName || userStore.linkedCatalogDetails.hasOwnProperty(attributeSystemName)) { // attributeSystemName for SKU is set as catalog code
          const currentCatalog = userStore.activeCatalog!.CatalogCode.toString() === attributeSystemName ? userStore.activeCatalog : userStore.linkedCatalogDetails[attributeSystemName]
          try {
            attributeValue = await appConfig.DB!.getModelSKUCount(article.ModelNumber, currentCatalog.CatalogCode)
          }
          catch (error) {
            console.error('Unable get model SKU count \n', error)
            attributeValue = attributePlaceHolder
          }
          attributeData.push(`${currentCatalog.Season} SKU: ${attributeValue}`)
        }
        else if (attributeSystemName === 'currentSKuCount' && userStore.activeCatalog) {
          try {
            attributeValue = await appConfig.DB!.getModelSKUCount(article.ModelNumber, userStore.activeCatalog.CatalogCode)
          }
          catch (error) {
            console.error('Unable get model SKU count \n', error)
            attributeValue = attributePlaceHolder
          }
          attributeData.push(`SKU Count: ${attributeValue}`)
        }
      }
    }
    return attributeData.join(attributesSeparator)
  }

  static async loadModelDetails(article: MyArticle, options?: IWbModelDetailsOptions) {
    const currentOptions = options || { modelNumber: article.ModelNumber }
    currentOptions.catalogCode = article.CatalogCode
    currentOptions.modelNumber = article.ModelNumber
    currentOptions.showLabels = utils.isDefined(options) && utils.isDefined(options.showLabels) ? options.showLabels : true
    currentOptions.attributes = options?.attributes?.length ? options.attributes : [...WbModelDetails.defaultAttributes]
    currentOptions.attributesSeparator = options?.attributesSeparator || '\n'
    currentOptions.attributePlaceHolder = options?.attributePlaceHolder || ''
    const modelDetailsObject = new WbModelDetails(await this.getText(article, currentOptions.showLabels, currentOptions.attributes, currentOptions.attributesSeparator, currentOptions.attributePlaceHolder), currentOptions)
    modelDetailsObject.article = article
    return modelDetailsObject
  }

  static async loadModelDetailsByNumber(modelNumber: string, catalogCode: number, options?: IWbModelDetailsOptions) {
    const userStore = useUserStore()
    if (!userStore || (!userStore.activeCatalog && !userStore.linkedCatalogDetails[catalogCode])) {
      console.warn('Unable to load model details by modelNumber')
      return null
    }
    const catalogDetails = userStore.linkedCatalogDetails[catalogCode] || userStore.activeCatalog
    const result = await appConfig.DB!.getMyArticleByModelNumber(modelNumber, catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, true)
    if (!result || !result.length || (result instanceof utils.CancelToken)) {
      console.warn('Failed to load model details by modelNumber', result)
      return null
    }
    return this.loadModelDetails(result[0], options)
  }

  private async getAttributesValue(modelNumber: string | null | undefined, catalogDetails: CatalogDetails, attributes: string[] | undefined, showLabels: boolean) {
    const userStore = useUserStore()
    let article: MyArticle | undefined
    if (modelNumber != null) {
      try {
        const articles = await appConfig.DB!.getMyArticleByModelNumber(modelNumber, catalogDetails, userStore.linkedCatalogDetails, userStore.myAttributes!, userStore.currentUsername, userStore.userProfile.Id, userStore.priceGroups.retail, userStore.priceGroups.wholesale, userStore.priceGroups.outlet, true)
        article = articles[0]
      }
      catch (error) {
        console.warn(error)
        article = this.article
      }
    }
    else if (this.article) {
      article = this.article
    }
    return await WbModelDetails.getText(article!, showLabels, attributes, '\n', '')
  }

  override _render(ctx: CanvasRenderingContext2D): void {
    super._render(ctx)
    if (this.selected && this.article) {
      ctx.font = `${this.fontSize}` + 'px Helvetica'
      // let seasonData = userStore.activeCatalog?.CatalogCode === this.article.CatalogCode
      // ? userStore.activeCatalog.Season
      // : userStore.linkedCatalogDetails[this.article.CatalogCode].Season
      // const seasonDataMeasure = ctx.measureText(seasonData)
      ctx.fillStyle = '#1484f8'
      // ctx.fillRect(this.width!/2 - seasonDataMeasure.width, -this.height!/2 , seasonDataMeasure.width, 15)
      ctx.fillStyle = '#FFFFFF'
      // ctx.fillText(seasonData, this.width!/2 - seasonDataMeasure.width, -this.height! / 2 + 12)
    }
  }
}

const f: any = fabric
f.WbModelDetails = WbModelDetails
